import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormOptions, FormResult } from './vform.models';
import { FormState } from './vform.store.service';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { ActivatedRoute, NavigationExtras, Params, Router } from '@angular/router';
import { UntypedFormGroup } from '@angular/forms';
import { removeRouterOutletsFromUrl } from '@vendasta/shared';

@Component({
  // TODO rename this to have the prefix vform
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'v-filter',
  templateUrl: './v-filter.component.html',
  styleUrls: ['./v-filter.component.scss'],
})
export class VFilterComponent implements OnInit, OnDestroy, OnChanges {
  @Input()
  formUrl: string;
  @Input()
  context: any;
  @Input()
  ignoredQueryParams: string[] = [];
  @Input()
  infiniteScroll: boolean;
  // Any changes to this input will trigger a reload of the formUrl
  @Input()
  reload: any;
  @Input()
  doInitialLoad = true;
  @Output()
  feedData = new EventEmitter<any>();
  @Output()
  formSelectionChanges = new EventEmitter<any>();

  formOptions: FormOptions;
  defaultState: any;

  private formGroup: UntypedFormGroup;
  private formSubmitFn: () => void;
  private initialized = false;
  private pastParams = '';
  private formSubmitSubscription: Subscription;
  private queryParamSubscription: Subscription;
  private cursor: number;
  private internalFeedData: any[] = [];
  private resetData: boolean;
  private loading = false;

  constructor(private route: ActivatedRoute, private router: Router) {}

  ngOnInit(): void {
    this.formOptions = {
      leftCustomButtons: [],
      formUrl: this.formUrl,
      context: this.context,
    };
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.reload?.previousValue !== changes?.reload?.currentValue && !!this.formSubmitFn) {
      this.resetData = true;
      this.formSubmitFn();
    }
  }

  ngOnDestroy(): void {
    if (this.formSubmitSubscription) {
      this.formSubmitSubscription.unsubscribe();
    }
    if (this.queryParamSubscription) {
      this.queryParamSubscription.unsubscribe();
    }
  }

  updateFeed(data: FormResult): void {
    // TODO: make feed_data an Input you can pass in?
    if (this.resetData) {
      this.internalFeedData = [...data.data.feed_data];
    } else {
      this.internalFeedData = [...this.internalFeedData, ...data.data.feed_data];
    }
    this.feedData.emit([...this.internalFeedData]);
    this.cursor = data.data.cursor;
    this.formGroup.patchValue(
      {
        cursor: data.data.cursor,
      },
      { emitEvent: false },
    );
    window.setTimeout(() => (this.loading = false), 100);
  }

  /**
   * Clears all the selected query params
   */
  clearAll(): void {
    const params = this.defaultState;
    this.formSelectionChanges.emit(params);
    this.updateQueryParams(params, false);
    this.formGroup.patchValue(params);
  }

  updateFormFromQueryParams(params: Params): void {
    if (!this.initialized) {
      return;
    }
    if (this.getCacheKey(params) === this.getCacheKey(this.formGroup.getRawValue())) {
      return;
    }

    // Delete any ignored query params from the list
    const keysToUse = Object.keys(params).filter((key) => this.ignoredQueryParams.indexOf(key) === -1);

    const ops = keysToUse
      .filter((param) => this.formGroup.contains(param))
      .reduce((previousValue, currentValue) => {
        return { ...previousValue, [currentValue]: params[currentValue] };
      }, {});

    const newString = this.getCacheKey(ops);
    if (this.pastParams === newString) {
      return;
    }
    this.pastParams = newString;
    if (params.cursor !== undefined) {
      this.cursor = params.cursor;
    }
    this.formGroup.patchValue(ops);
  }

  handleFormEvent(state: FormState): void {
    if (!!this.formSubmitFn && state.formReady === true) {
      if (!this.initialized) {
        this.initialized = true;

        this.formGroup = state.formGroup;
        this.queryParamSubscription = this.route.queryParams.subscribe(this.updateFormFromQueryParams.bind(this));

        const debouncedForm$ = state.formGroup.valueChanges.pipe(
          distinctUntilChanged(this.formChanged),
          debounceTime(500),
        );

        // Get the initial value
        this.defaultState = state.formGroup.value;
        this.formSelectionChanges.emit(this.defaultState);
        this.updateQueryParams(this.defaultState);

        if (this.doInitialLoad) {
          this.formSubmitFn();
        }

        // Listen for any changes to the form and submit the new filter
        this.formSubmitSubscription = debouncedForm$.subscribe({
          next: () => {
            this.resetData = true;
            this.formSubmitFn();
          },
        });
        debouncedForm$.subscribe((formChanges) => {
          this.formSelectionChanges.emit(formChanges);
          this.updateQueryParams(formChanges);
        });
      }
    }
  }

  onScroll(): void {
    // If infiniteScroll is enabled, we aren't already loading data, and we have a cursor to go to, keep loading more data.
    if (!!this.infiniteScroll && !this.loading && !!this.cursor) {
      this.resetData = false;
      this.formSubmitFn();
    }
  }

  // Grab the form submit function from the inner vform component so that we can submit the form whenever we wish
  handleFormSubmitFnEvent(fn: () => void): void {
    this.formSubmitFn = () => {
      this.loading = true;
      fn();
    };
  }

  // Custom serialization function that orders keys and filters out null or undefined values.
  private getCacheKey(obj: any): string {
    return JSON.stringify(
      Object.keys(obj)
        .filter((key) => typeof obj[key] !== 'undefined' && obj[key] !== null)
        .sort((a, b) => {
          if (a < b) {
            return 1;
          }
          if (a > b) {
            return -1;
          }
          return 0;
        })
        .reduce((previousValue, currentValue) => {
          return { ...previousValue, [currentValue]: obj[currentValue] };
        }, {}),
    );
  }

  private updateQueryParams(params: Record<string, any>, withPrevious = true): void {
    let previous = {};
    if (withPrevious) {
      previous = this.route.snapshot.queryParams;
    }

    const navigationExtras: NavigationExtras = {
      queryParams: { ...previous, ...params },
    };
    this.router.navigate([removeRouterOutletsFromUrl(window.location.pathname)], navigationExtras);
  }

  /**
   * Compares the form value and makes sure there is a change
   */
  private formChanged(a: any, b: any): boolean {
    const aKeys = Object.keys(a),
      bKeys = Object.keys(b);
    // If the keys length do not match return
    if (aKeys.length !== bKeys.length) {
      return true;
    }

    let noChanges = true;
    aKeys.forEach((key) => {
      // Treat undefined and null as the same
      if (typeof a[key] === 'undefined' || a[key] === null) {
        const bIsNull = typeof b[key] === 'undefined' || b[key] === null;
        if (!bIsNull) {
          noChanges = false;
          return;
        }
      }
      // If the keys do not match
      if (a[key] !== b[key]) {
        noChanges = false;
      }
    });

    return noChanges;
  }
}
